import { useEffect, useState } from "react";
import { getAuthHeaders, useAuth } from "../contexts/AuthContext";

interface Player {
	id: string;
	name: string;
	elo: number;
	wins: number;
	losses: number;
}

interface Matchup {
	opponentId: string;
	opponentName: string;
	opponentElo: number;
	wins: number;
	losses: number;
}

interface ApiMatchup {
	opponent_id: string;
	opponent_name: string;
	opponent_elo: number;
	wins: number;
	losses: number;
}

interface EloDataPoint {
	timestamp: number;
	elo: number;
}

interface PlayerStatsProps {
	playerId: string;
	onBack: () => void;
}

const DEFAULT_ELO = 1500;

function EloChart({ data }: { data: EloDataPoint[] }) {
	const [activePoint, setActivePoint] = useState<number | null>(null);

	if (data.length === 0) {
		return <p className="hint">No match history yet</p>;
	}

	const width = 500;
	const height = 200;
	const padding = { top: 20, right: 20, bottom: 30, left: 50 };
	const chartWidth = width - padding.left - padding.right;
	const chartHeight = height - padding.top - padding.bottom;

	// Add starting point at 1500 ELO (match 0)
	const allElos = [DEFAULT_ELO, ...data.map((d) => d.elo)];
	const totalMatches = data.length;

	const minElo = Math.min(...allElos);
	const maxElo = Math.max(...allElos);
	const eloRange = maxElo - minElo || 100;
	const eloPadding = eloRange * 0.1;
	const yMin = Math.floor((minElo - eloPadding) / 10) * 10;
	const yMax = Math.ceil((maxElo + eloPadding) / 10) * 10;

	// X scale based on match index (0 = start, 1 = first match, etc.)
	const xScale = (matchIndex: number) =>
		padding.left + (matchIndex / totalMatches) * chartWidth;
	const yScale = (elo: number) =>
		padding.top + ((yMax - elo) / (yMax - yMin)) * chartHeight;

	const pathData = allElos
		.map((elo, i) => `${i === 0 ? "M" : "L"} ${xScale(i)} ${yScale(elo)}`)
		.join(" ");

	// Y-axis ticks
	const yTicks: number[] = [];
	const tickStep = Math.ceil((yMax - yMin) / 5 / 10) * 10;
	for (let tick = yMin; tick <= yMax; tick += tickStep) {
		yTicks.push(tick);
	}

	const currentElo = allElos[allElos.length - 1];
	const eloChange = currentElo - DEFAULT_ELO;

	return (
		<div className="elo-chart">
			<svg
				viewBox={`0 0 ${width} ${height}`}
				preserveAspectRatio="xMidYMid meet"
				role="img"
				aria-label="ELO rating over time chart"
			>
				{/* Grid lines */}
				{yTicks.map((tick) => (
					<line
						key={tick}
						x1={padding.left}
						y1={yScale(tick)}
						x2={width - padding.right}
						y2={yScale(tick)}
						stroke="var(--border-color)"
						strokeDasharray="4,4"
					/>
				))}

				{/* 1500 baseline */}
				<line
					x1={padding.left}
					y1={yScale(DEFAULT_ELO)}
					x2={width - padding.right}
					y2={yScale(DEFAULT_ELO)}
					stroke="var(--text-muted)"
					strokeWidth="1"
					strokeDasharray="2,2"
				/>

				{/* Y-axis */}
				<line
					x1={padding.left}
					y1={padding.top}
					x2={padding.left}
					y2={height - padding.bottom}
					stroke="var(--border-color)"
				/>

				{/* Y-axis labels */}
				{yTicks.map((tick) => (
					<text
						key={tick}
						x={padding.left - 8}
						y={yScale(tick)}
						textAnchor="end"
						dominantBaseline="middle"
						fill="var(--text-muted)"
						fontSize="11"
					>
						{tick}
					</text>
				))}

				{/* X-axis */}
				<line
					x1={padding.left}
					y1={height - padding.bottom}
					x2={width - padding.right}
					y2={height - padding.bottom}
					stroke="var(--border-color)"
				/>

				{/* X-axis labels */}
				<text
					x={padding.left}
					y={height - padding.bottom + 16}
					textAnchor="start"
					fill="var(--text-muted)"
					fontSize="11"
				>
					Start
				</text>
				<text
					x={width - padding.right}
					y={height - padding.bottom + 16}
					textAnchor="end"
					fill="var(--text-muted)"
					fontSize="11"
				>
					{totalMatches} {totalMatches === 1 ? "match" : "matches"}
				</text>

				{/* Line path */}
				<path
					d={pathData}
					fill="none"
					stroke="url(#eloGradient)"
					strokeWidth="2.5"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>

				{/* Gradient definition */}
				<defs>
					<linearGradient id="eloGradient" x1="0%" y1="0%" x2="100%" y2="0%">
						<stop offset="0%" stopColor="var(--accent-primary)" />
						<stop offset="100%" stopColor="var(--accent-secondary)" />
					</linearGradient>
				</defs>

				{/* Data points */}
				{data.map((point, i) => (
					<circle
						key={point.timestamp}
						className={`elo-chart-point ${activePoint === i ? "active" : ""}`}
						cx={xScale(i + 1)}
						cy={yScale(point.elo)}
						r={activePoint === i ? 5 : 3}
						fill="var(--accent-primary)"
						onPointerEnter={() => setActivePoint(i)}
						onPointerLeave={() => setActivePoint(null)}
						onPointerDown={() => setActivePoint(activePoint === i ? null : i)}
					/>
				))}

				{/* Tooltip */}
				{activePoint !== null && (
					<g className="elo-chart-tooltip" pointerEvents="none">
						<rect
							x={xScale(activePoint + 1) - 40}
							y={yScale(data[activePoint].elo) - 28}
							width="80"
							height="20"
							rx="4"
							fill="var(--bg-secondary)"
							stroke="var(--border-color)"
						/>
						<text
							x={xScale(activePoint + 1)}
							y={yScale(data[activePoint].elo) - 14}
							textAnchor="middle"
							fill="var(--text-primary)"
							fontSize="11"
							fontWeight="500"
						>
							#{activePoint + 1}: {data[activePoint].elo} ELO
						</text>
					</g>
				)}

				{/* Current ELO label */}
				<text
					x={width - padding.right}
					y={padding.top - 5}
					textAnchor="end"
					fill={eloChange >= 0 ? "var(--color-success)" : "var(--color-danger)"}
					fontSize="12"
					fontWeight="600"
				>
					{eloChange >= 0 ? "+" : ""}
					{eloChange} from start
				</text>
			</svg>
		</div>
	);
}

export function PlayerStats({ playerId, onBack }: PlayerStatsProps) {
	const { token } = useAuth();
	const [player, setPlayer] = useState<Player | null>(null);
	const [matchups, setMatchups] = useState<Matchup[]>([]);
	const [eloHistory, setEloHistory] = useState<EloDataPoint[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		async function fetchStats() {
			try {
				const res = await fetch(`/api/stats/player/${playerId}`, {
					headers: getAuthHeaders(token),
				});
				const data = await res.json();
				setPlayer(data.player);
				setMatchups(
					data.matchups.map((m: ApiMatchup) => ({
						opponentId: m.opponent_id,
						opponentName: m.opponent_name,
						opponentElo: m.opponent_elo,
						wins: m.wins,
						losses: m.losses,
					})),
				);
				setEloHistory(data.eloHistory || []);
			} catch (e) {
				console.error("Failed to fetch player stats:", e);
			} finally {
				setLoading(false);
			}
		}
		fetchStats();
	}, [playerId, token]);

	if (loading) {
		return (
			<div className="card player-stats">
				<p className="hint">Loading...</p>
			</div>
		);
	}

	if (!player) {
		return (
			<div className="card player-stats">
				<p className="hint">Player not found</p>
				<button type="button" onClick={onBack} className="btn-back">
					Back
				</button>
			</div>
		);
	}

	const winRate =
		player.wins + player.losses > 0
			? ((player.wins / (player.wins + player.losses)) * 100).toFixed(0)
			: "0";

	return (
		<div className="player-stats-page">
			<button type="button" onClick={onBack} className="btn-back">
				&larr; Back to Leaderboard
			</button>

			<div className="player-stats-columns">
				<div className="player-stats-left">
					<div className="card player-stats-header">
						<h2>{player.name}</h2>
						<div className="player-stats-overview">
							<div className="stat-box">
								<span className="stat-value">{player.elo}</span>
								<span className="stat-label">ELO</span>
							</div>
							<div className="stat-box">
								<span className="stat-value">{player.wins}</span>
								<span className="stat-label">Wins</span>
							</div>
							<div className="stat-box">
								<span className="stat-value">{player.losses}</span>
								<span className="stat-label">Losses</span>
							</div>
							<div className="stat-box">
								<span className="stat-value">{winRate}%</span>
								<span className="stat-label">Win Rate</span>
							</div>
						</div>
					</div>

					<div className="card player-stats-chart">
						<h3>ELO Over Time</h3>
						<EloChart data={eloHistory} />
					</div>
				</div>

				<div className="player-stats-right">
					<div className="card player-stats-matchups">
						<h3>Head-to-Head Records</h3>
						{matchups.length === 0 ? (
							<p className="hint">No matches played yet</p>
						) : (
							<div className="matchups-list">
								{matchups.map((matchup) => {
									const total = matchup.wins + matchup.losses;
									const winPct = (matchup.wins / total) * 100;
									const isWinning = matchup.wins > matchup.losses;
									const isLosing = matchup.losses > matchup.wins;

									return (
										<div key={matchup.opponentId} className="matchup-item">
											<div className="matchup-opponent">
												<span className="opponent-name">
													{matchup.opponentName}
												</span>
												<span className="opponent-elo">
													{matchup.opponentElo} ELO
												</span>
											</div>
											<div className="matchup-record">
												<span
													className={
														isWinning ? "winning" : isLosing ? "losing" : ""
													}
												>
													{matchup.wins}W - {matchup.losses}L
												</span>
											</div>
											<div className="matchup-bar">
												<div
													className={`matchup-bar-fill ${isWinning ? "winning" : isLosing ? "losing" : "even"}`}
													style={{ width: `${winPct}%` }}
												/>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
